import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { MarkdownContent } from '../components/MarkdownContent';
import { QuickReferenceCard } from '../components/QuickReferenceCard';
import { ComparisonTable } from '../components/ComparisonTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserCheck, Award, UserPlus, Calendar, Wrench, Users2, Info } from 'lucide-react';

export const TechniciansGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Technician Management'
      description='Manage technicians and their assignments'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Technician Management' }
      ]}
    >
      <MarkdownContent>
        <div className='max-w-[1000px]'>
          <div className='not-prose mb-6 flex items-start gap-3 p-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950'>
            <Info className='h-6 w-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0' />
            <div className='flex-1'>
              <p className='font-semibold mb-1 text-blue-900 dark:text-blue-100 text-sm'>
                What is Technician Management?
              </p>
              <p className='text-sm text-blue-800 dark:text-blue-200'>
                Technician Management is your team roster where you add, edit, and organize the
                people who complete recall work. You can set their skill levels, mark them as active
                or inactive, track their workload, and manage their schedules. Think of it as HR for
                your recall technician team.
              </p>
            </div>
          </div>

          <section className='mb-20' id='why-technician-management-matters'>
            <h2 className='text-3xl font-bold mb-6'>Why Technician Management Matters</h2>
            <p className='text-sm leading-6 mb-6'>
              Having accurate technician information helps Vehix match the right work to the right
              person:
            </p>

            <div className='not-prose my-8 grid gap-4 md:grid-cols-2'>
              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Award className='h-5 w-5 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Match Skills to Work</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm'>
                  <p>
                    When you set skill levels correctly, complex recalls go to experienced
                    technicians and simple work can be done by anyone. No more "wrong person for the
                    job" situations.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Users2 className='h-5 w-5 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Balance Workload</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm'>
                  <p>
                    See how many active assignments each technician has at a glance. Distribute work
                    fairly so nobody is overwhelmed or idle.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Calendar className='h-5 w-5 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Manage Availability</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm'>
                  <p>
                    Mark technicians as inactive when they are on vacation, leave, or no longer with
                    your company. They won't receive new assignments during that time.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Wrench className='h-5 w-5 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Track Specializations</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm'>
                  <p>
                    Document which technicians are certified for specific campaign types or have
                    special expertise, making assignment decisions easier.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className='mb-20' id='how-to-access-technician-management'>
            <h2 className='text-3xl font-bold mb-6 flex items-center'>
              <UserCheck className='inline h-8 w-8 mr-2 text-primary' />
              How to Access Technician Management
            </h2>
            <div className='not-prose mb-6 space-y-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  1
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Open the Sidebar</p>
                  <p className='text-sm text-muted-foreground'>
                    Look at the left side of your screen for the main navigation menu.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  2
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Find Configuration Section</p>
                  <p className='text-sm text-muted-foreground'>
                    Scroll down until you see "Configuration". This section contains settings that
                    affect how Vehix works.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  3
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Click Technicians</p>
                  <p className='text-sm text-muted-foreground'>
                    Under Configuration, click "Technicians". You will see your complete technician
                    roster.
                  </p>
                </div>
              </div>
            </div>

            <div className='not-prose mb-6 flex items-start gap-3 p-4 rounded-lg border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950'>
              <Info className='h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-yellow-900 dark:text-yellow-100 text-sm'>
                  Who Can Access This?
                </p>
                <p className='text-sm text-yellow-800 dark:text-yellow-200'>
                  Technician Management is typically restricted to Service Managers, Field
                  Operations Directors, and Dealership Admins. Regular technicians usually can't see
                  or edit this page since it contains sensitive information about the whole team.
                  Contact your administrator if you need access.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='understanding-the-technician-list'>
            <h2 className='text-3xl font-bold mb-6'>Understanding the Technician List</h2>
            <p className='text-sm leading-6 mb-4'>
              The Technician Management page shows all your technicians in a table with:
            </p>
            <ul className='text-sm leading-6 space-y-2'>
              <li>
                <strong>Name:</strong> Technician's full name
              </li>
              <li>
                <strong>Skill Level:</strong> High, Medium, or Low capability rating
              </li>
              <li>
                <strong>Status:</strong> Active (available for work) or Inactive (unavailable)
              </li>
              <li>
                <strong>Contact Info:</strong> Phone number and email
              </li>
              <li>
                <strong>Current Load:</strong> Number of active assignments
              </li>
              <li>
                <strong>Work Schedule:</strong> Days and hours they are available (if configured)
              </li>
            </ul>

            <div className='not-prose mt-6 flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30'>
              <Info className='h-6 w-6 text-muted-foreground mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='text-sm'>
                  <strong>Quick Tip:</strong> Click on a technician's row to expand it and see more
                  details like notes, specializations, and assignment history.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='understanding-skill-levels'>
            <h2 className='text-3xl font-bold mb-6'>Understanding Skill Levels</h2>
            <p className='text-sm leading-6 mb-6'>
              Skill levels help Vehix suggest the right technician for each recall. Here's what each
              level means:
            </p>

            <ComparisonTable
              rowHeaders={[
                'Experience level',
                'Complex diagnostics',
                'ECU programming',
                'Works independently',
                'Can supervise others',
                'Typical campaigns'
              ]}
              columns={[
                {
                  header: 'High Skill',
                  values: ['5+ years', true, true, true, true, 'All types, complex repairs']
                },
                {
                  header: 'Medium Skill',
                  values: [
                    '2-5 years',
                    true,
                    false,
                    true,
                    false,
                    'Standard repairs, most campaigns'
                  ]
                },
                {
                  header: 'Low Skill',
                  values: [
                    '0-2 years',
                    false,
                    false,
                    false,
                    false,
                    'Simple repairs, software updates'
                  ]
                }
              ]}
            />

            <h3 className='text-lg font-semibold mt-8 mb-4'>
              High Skill (Level 1) - Master Technicians
            </h3>
            <p className='text-sm leading-6 mb-3'>
              These are your most experienced, highest-trained technicians. They can handle
              anything:
            </p>
            <ul className='text-sm leading-6 space-y-1 mb-4'>
              <li>Complex diagnostic work requiring advanced tools</li>
              <li>ECU reprogramming and software flashing</li>
              <li>Major component replacement (engines, transmissions, airbags)</li>
              <li>Training and supervising other technicians</li>
              <li>Problem-solving when manufacturer instructions are unclear</li>
            </ul>
            <p className='text-sm text-muted-foreground italic mb-6'>
              <strong>When to use:</strong> High-skill technicians should get the toughest, most
              critical recalls. Don't waste their expertise on simple work unless you have excess
              capacity.
            </p>

            <h3 className='text-lg font-semibold mt-6 mb-4'>
              Medium Skill (Level 2) - Experienced Technicians
            </h3>
            <p className='text-sm leading-6 mb-3'>
              Your reliable, competent technicians who handle most day-to-day recall work:
            </p>
            <ul className='text-sm leading-6 space-y-1 mb-4'>
              <li>Standard repair procedures following manufacturer instructions</li>
              <li>Moderate complexity diagnostics</li>
              <li>Most recall campaigns after receiving appropriate training</li>
              <li>Can work independently but may need occasional guidance</li>
            </ul>
            <p className='text-sm text-muted-foreground italic mb-6'>
              <strong>When to use:</strong> Medium-skill technicians are your workhorses. They
              should get the majority of standard recalls that don't require specialized expertise.
            </p>

            <h3 className='text-lg font-semibold mt-6 mb-4'>
              Low Skill (Level 3) - Entry-Level Technicians
            </h3>
            <p className='text-sm leading-6 mb-3'>
              Newer technicians who are learning and developing. They can handle straightforward
              work:
            </p>
            <ul className='text-sm leading-6 space-y-1 mb-4'>
              <li>Simple part replacements (clips, covers, labels)</li>
              <li>Software updates requiring minimal technical judgment</li>
              <li>Visual inspections and basic diagnostics</li>
              <li>Work that requires supervision or double-checking</li>
            </ul>
            <p className='text-sm text-muted-foreground italic mb-6'>
              <strong>When to use:</strong> Low-skill technicians should receive simple recalls to
              build experience. Avoid assigning complex or safety-critical work until they are
              properly trained.
            </p>

            <div className='not-prose flex items-start gap-3 p-4 rounded-lg border-l-4 border-green-500 bg-green-50 dark:bg-green-950'>
              <Info className='h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-green-900 dark:text-green-100 text-sm'>
                  Skill Levels Grow Over Time
                </p>
                <p className='text-sm text-green-800 dark:text-green-200'>
                  Don't keep technicians at "Low Skill" forever! As they gain experience, complete
                  training, and prove themselves, promote them to Medium or High. Review skill
                  levels during annual performance reviews or after major certifications.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='active-vs-inactive-status'>
            <h2 className='text-3xl font-bold mb-6'>Active vs. Inactive Status</h2>
            <p className='text-sm leading-6 mb-6'>
              Status controls whether a technician can receive new work assignments. It is simple
              but powerful:
            </p>

            <div className='not-prose my-6 space-y-4'>
              <div className='flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950 rounded-lg border-l-4 border-green-500'>
                <UserCheck className='h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0' />
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-green-900 dark:text-green-100 text-sm'>
                    Active Status
                  </p>
                  <p className='text-sm text-green-800 dark:text-green-200 mb-2'>
                    The technician is available for work and can receive new assignments.
                  </p>
                  <p className='text-sm text-green-700 dark:text-green-300'>
                    <strong>Effects:</strong> Appears in routing suggestions, shows up in assignment
                    dropdowns, visible in all technician filters and lists.
                  </p>
                </div>
              </div>

              <div className='flex items-start gap-3 p-4 bg-muted/30 rounded-lg border-l-4 border-border'>
                <Calendar className='h-6 w-6 text-muted-foreground mt-1 flex-shrink-0' />
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Inactive Status</p>
                  <p className='text-sm text-muted-foreground mb-2'>
                    The technician is not available for new work (vacation, leave, or no longer
                    employed).
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    <strong>Effects:</strong> Hidden from routing suggestions, does not receive new
                    assignments, still visible in reports for historical data.
                  </p>
                </div>
              </div>
            </div>

            <h3 className='text-lg font-semibold mt-8 mb-4'>When to Mark a Technician Inactive</h3>
            <ul className='text-sm leading-6 space-y-2 mb-6'>
              <li>
                <strong>Vacation or PTO:</strong> Set to Inactive before they leave, add note with
                return date, set reminder to reactivate
              </li>
              <li>
                <strong>Medical Leave:</strong> Inactive during leave with note "Medical leave -
                return TBD"
              </li>
              <li>
                <strong>Termination:</strong> Immediately set to Inactive, reassign their pending
                work to others
              </li>
              <li>
                <strong>Temporary Reassignment:</strong> If pulled to another department for a
                project, set Inactive until they return
              </li>
            </ul>

            <div className='not-prose flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30'>
              <Info className='h-6 w-6 text-muted-foreground mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-sm'>Inactive ≠ Deleted</p>
                <p className='text-sm text-muted-foreground'>
                  Making a technician Inactive does not delete them or their history. It just
                  prevents new assignments. You can reactivate them anytime by flipping the status
                  back to Active. This preserves all their past work and makes it easy to bring them
                  back.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='adding-a-new-technician'>
            <h2 className='text-3xl font-bold mb-6 flex items-center'>
              <UserPlus className='inline h-8 w-8 mr-2 text-primary' />
              Adding a New Technician
            </h2>
            <p className='text-sm leading-6 mb-6'>
              When you hire someone new or bring a technician into the recall program, here's how to
              add them:
            </p>

            <div className='not-prose mb-6 space-y-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  1
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Click "Add Technician" Button</p>
                  <p className='text-sm text-muted-foreground'>
                    Look for this button near the top of the Technician Management page. It will
                    open a form.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  2
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Enter Basic Information</p>
                  <p className='text-sm text-muted-foreground'>
                    Fill in their full name, phone number, and email address. All three are usually
                    required.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  3
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Assign Skill Level</p>
                  <p className='text-sm text-muted-foreground mb-2'>
                    Choose High, Medium, or Low based on their experience and certifications. When
                    in doubt, start conservative (Lower skill) and promote later.
                  </p>
                  <p className='text-sm text-muted-foreground italic'>
                    New hires typically start at Low or Medium. Experienced techs transferring from
                    other departments might start at Medium or High.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  4
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Set Work Schedule (If Available)</p>
                  <p className='text-sm text-muted-foreground'>
                    If your system supports schedules, enter their work days and hours. This helps
                    with routing efficiency.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  5
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Configure Service Area (Optional)</p>
                  <p className='text-sm text-muted-foreground'>
                    Some systems let you set geographic boundaries. If your technician only covers
                    certain ZIP codes, configure that here.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  6
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>
                    Add Campaign Specializations (Optional)
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    If they have special certifications (like EV recalls, airbag certified),
                    document it in the specializations field.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  7
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Save and Verify</p>
                  <p className='text-sm text-muted-foreground'>
                    Click Save. The technician should immediately appear in your Active technicians
                    list and be available for assignments.
                  </p>
                </div>
              </div>
            </div>

            <div className='not-prose flex items-start gap-3 p-4 rounded-lg border-l-4 border-green-500 bg-green-50 dark:bg-green-950'>
              <Info className='h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='text-sm text-green-800 dark:text-green-200'>
                  <strong>Onboarding Tip:</strong> After adding a new technician, create a small
                  test route with 1-2 easy recalls and assign it to them. This verifies their
                  account works and gives them a simple first assignment to build confidence.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='common-technician-management-workflows'>
            <h2 className='text-3xl font-bold mb-6'>Common Technician Management Workflows</h2>

            <h3 className='text-lg font-semibold mb-4'>
              Scenario 1: Promoting a Technician (Skill Level Increase)
            </h3>
            <p className='text-sm leading-6 mb-4'>
              A technician has completed certifications and is ready for more complex work:
            </p>
            <div className='not-prose mb-8 space-y-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  1
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Review Performance</p>
                  <p className='text-sm text-muted-foreground'>
                    Look at their completion rate, feedback from customers/dealers, and any formal
                    certifications they have earned.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  2
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Find the Technician</p>
                  <p className='text-sm text-muted-foreground'>
                    Locate them in the Technician Management list and click to edit.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  3
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Update Skill Level</p>
                  <p className='text-sm text-muted-foreground'>
                    Change from Low → Medium or Medium → High based on their readiness. This is a
                    significant change - make sure they are truly ready.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  4
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Document the Promotion</p>
                  <p className='text-sm text-muted-foreground'>
                    Add a note: "Promoted to [skill level] on [date] after completing
                    [certification/training]. Can now handle [types of recalls]."
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  5
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Notify Stakeholders</p>
                  <p className='text-sm text-muted-foreground'>
                    Tell the technician about their promotion, update your service manager, and
                    adjust assignment strategies to give them appropriate work.
                  </p>
                </div>
              </div>
            </div>

            <h3 className='text-lg font-semibold mb-4'>Scenario 2: Managing Vacation/Time Off</h3>
            <p className='text-sm leading-6 mb-4'>
              A technician is taking 2 weeks of vacation. Here's the workflow:
            </p>
            <ol className='text-sm leading-6 space-y-4 mb-6'>
              <li>
                <strong>Before They Leave:</strong>
                <ul className='mt-2 space-y-1 ml-4'>
                  <li>Find the technician in the list</li>
                  <li>Change Status from Active to Inactive</li>
                  <li>
                    Add note: "Vacation - [start date] to [end date]. Returns [date]. Contact in
                    emergency: [phone]."
                  </li>
                  <li>
                    Review their pending assignments and reassign urgent ones to available
                    technicians
                  </li>
                </ul>
              </li>
              <li>
                <strong>While They're Gone:</strong>
                <ul className='mt-2 space-y-1 ml-4'>
                  <li>They won't appear in routing suggestions or receive new assignments</li>
                  <li>
                    Their existing assignments stay visible so you can track or reassign them if
                    needed
                  </li>
                </ul>
              </li>
              <li>
                <strong>When They Return:</strong>
                <ul className='mt-2 space-y-1 ml-4'>
                  <li>Change Status back to Active</li>
                  <li>Update or clear the vacation note</li>
                  <li>They immediately become available for new assignments</li>
                </ul>
              </li>
            </ol>

            <div className='not-prose mb-8 flex items-start gap-3 p-4 rounded-lg border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950'>
              <Info className='h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-yellow-900 dark:text-yellow-100 text-sm'>
                  Don't Forget to Reactivate!
                </p>
                <p className='text-sm text-yellow-800 dark:text-yellow-200'>
                  Set a calendar reminder for yourself to reactivate technicians when they return
                  from vacation. Forgetting leaves them invisible in the system and can cause
                  confusion when they wonder why they are not getting assignments!
                </p>
              </div>
            </div>

            <h3 className='text-lg font-semibold mb-4'>Scenario 3: Handling a Termination</h3>
            <p className='text-sm leading-6 mb-4'>
              A technician leaves your company (quits, fired, or retires):
            </p>
            <div className='not-prose mb-8 space-y-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  1
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Immediately Set to Inactive</p>
                  <p className='text-sm text-muted-foreground'>
                    As soon as you know they are leaving, change their status to Inactive so they
                    stop receiving new assignments.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  2
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Reassign Active Work</p>
                  <p className='text-sm text-muted-foreground'>
                    Review their current assignments. Reassign any pending or in-progress recalls to
                    other active technicians.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  3
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Add Final Note</p>
                  <p className='text-sm text-muted-foreground'>
                    Document: "Separated from company on [date]. Reason: [voluntary
                    separation/termination/retirement]." This helps future reference.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold'>
                  4
                </div>
                <div className='flex-1'>
                  <p className='font-semibold mb-1 text-sm'>Keep Account Intact</p>
                  <p className='text-sm text-muted-foreground mb-2'>
                    Do NOT delete the technician account. Keeping it preserves historical data,
                    completion records, and helps with reporting.
                  </p>
                  <p className='text-sm text-muted-foreground italic'>
                    Even though they are Inactive, their past work history remains visible for
                    audits, reports, and understanding completion rates.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className='mb-20' id='monitoring-workload-balance'>
            <h2 className='text-3xl font-bold mb-6'>Monitoring Workload Balance</h2>
            <p className='text-sm leading-6 mb-4'>
              The "Current Load" column shows how many active assignments each technician has. Use
              this to distribute work fairly:
            </p>
            <ul className='text-sm leading-6 space-y-2 mb-6'>
              <li>
                <strong>Balanced is Better:</strong> If one tech has 15 assignments and another has
                2, consider redistributing
              </li>
              <li>
                <strong>Account for Skill:</strong> High-skill technicians might have fewer
                assignments if they are more complex
              </li>
              <li>
                <strong>Watch for Overload:</strong> If someone consistently has 20+ assignments,
                they are probably falling behind
              </li>
              <li>
                <strong>Identify Underutilization:</strong> Technicians with consistently 0-1
                assignments might need more work or training
              </li>
            </ul>

            <div className='not-prose flex items-start gap-3 p-4 rounded-lg border-l-4 border-green-500 bg-green-50 dark:bg-green-950'>
              <Info className='h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0' />
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-green-900 dark:text-green-100 text-sm'>
                  Weekly Review
                </p>
                <p className='text-sm text-green-800 dark:text-green-200'>
                  Every Monday morning, spend 5 minutes reviewing the Current Load column. This
                  quick check helps you spot imbalances before they become problems. Aim for rough
                  equality adjusted for skill level and complexity.
                </p>
              </div>
            </div>
          </section>

          <section className='mb-20' id='quick-reference'>
            <h2 className='text-3xl font-bold mb-6'>Quick Reference</h2>
            <QuickReferenceCard
              title='Technician Management Quick Guide'
              items={[
                {
                  command: 'High Skill',
                  description: '5+ years, can do anything - assign complex work'
                },
                {
                  command: 'Medium Skill',
                  description: '2-5 years, your workhorses - assign standard work'
                },
                {
                  command: 'Low Skill',
                  description: '0-2 years, learning - assign simple work only'
                },
                { command: 'Active', description: 'Available for assignments - shows in routing' },
                { command: 'Inactive', description: 'Unavailable - hidden from new assignments' }
              ]}
            />
          </section>

          <section className='mb-20' id='common-questions'>
            <h2 className='text-3xl font-bold mb-6'>Common Questions</h2>

            <h3 className='text-lg font-semibold mb-3'>
              How do I decide between Medium and High skill for an experienced technician?
            </h3>
            <p className='text-sm leading-6 mb-2'>Ask yourself:</p>
            <ul className='text-sm leading-6 space-y-1 mb-4'>
              <li>Can they handle the absolute most complex recalls without any support? (High)</li>
              <li>Can they train and supervise other technicians? (High)</li>
              <li>Do they sometimes need guidance on unusual situations? (Medium)</li>
              <li>Are there certain types of recalls they shouldn't do alone? (Medium)</li>
            </ul>
            <p className='text-sm leading-6 mb-6'>
              When in doubt, start at Medium. You can always promote to High after seeing consistent
              success with complex work.
            </p>

            <h3 className='text-lg font-semibold mb-3'>
              Can I change a technician's skill level down (like High to Medium)?
            </h3>
            <p className='text-sm leading-6 mb-6'>
              Technically yes, but this is rare and sensitive. It usually only happens if a
              technician's certifications lapse or there are serious performance issues. If you need
              to do this, coordinate with HR and have a documented reason. It is better to provide
              retraining and keep them at their current level.
            </p>

            <h3 className='text-lg font-semibold mb-3'>
              What if a technician disagrees with their assigned skill level?
            </h3>
            <p className='text-sm leading-6 mb-6'>
              Have an honest conversation about expectations. Explain what's required for each skill
              level. Offer a clear path to promotion: "Complete these certifications, demonstrate
              success with X recalls, and we'll review for promotion in 3 months." Document the
              conversation and follow through.
            </p>

            <h3 className='text-lg font-semibold mb-3'>How many active technicians do I need?</h3>
            <p className='text-sm leading-6 mb-6'>
              This depends on your recall volume. A rough guideline: one full-time technician can
              handle about 20-30 mobile recalls per week or 40-50 in-facility recalls per week,
              depending on complexity and distance. Monitor your team's completion rates and adjust
              staffing accordingly.
            </p>
          </section>

          <section className='mb-20' id='next-steps'>
            <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
            <p className='text-sm leading-6 mb-4'>
              Now that you understand Technician Management, learn about:
            </p>
            <ul className='text-sm leading-6 space-y-2'>
              <li>
                <Link to='/docs/features/campaigns'>Campaign Management</Link> - Match campaign
                skill requirements to technician skill levels
              </li>
              <li>
                <Link to='/docs/features/manual-routing'>Manual Routing</Link> - Assign recalls to
                the right technicians based on their skills
              </li>
              <li>
                <Link to='/docs/features/route-tracker'>Route Tracker</Link> - Monitor your
                technicians' progress and completion rates
              </li>
            </ul>
          </section>

          <div className='not-prose flex items-start gap-3 p-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950'>
            <Info className='h-6 w-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0' />
            <div className='flex-1'>
              <p className='text-sm text-blue-800 dark:text-blue-200'>
                <strong>Need Help?</strong> Technician Management affects your whole team's
                efficiency. If you are unsure about skill level assignments or status changes, reach
                out to <a href='mailto:contact@kebi.ai'>contact@kebi.ai</a> - we are happy to help
                you make the right decisions!
              </p>
            </div>
          </div>
        </div>
      </MarkdownContent>
    </DocsLayout>
  );
};
